import { useEffect, useRef, useState } from 'react'
import { useTreeStore } from '../../stores/tree.js'
import { api } from '../../services/api.js'
import PlaceInput from '../shared/PlaceInput.jsx'
import PartialDateInput from '../shared/PartialDateInput.jsx'

// When opened from a person page, `anchorPerson` is that person.
// The user picks HOW that person relates to someone else.
// This is more natural than abstract "Person A / Person B".

const ROLES = [
  {
    value: 'child_of',
    label: 'Add a parent',
    hint: (name) => `${name} is the child. Pick a parent.`,
    // anchor = child (person_b), other = parent (person_a)
    build: (anchorId, otherId) => ({ type: 'parent_child', person_a_id: otherId, person_b_id: anchorId }),
  },
  {
    value: 'parent_of',
    label: 'Add a child',
    hint: (name) => `${name} is the parent. Pick a child.`,
    // anchor = parent (person_a), other = child (person_b)
    build: (anchorId, otherId) => ({ type: 'parent_child', person_a_id: anchorId, person_b_id: otherId }),
  },
  {
    value: 'partner',
    label: 'Add a partner',
    hint: (name) => `${name} and the selected person are partners.`,
    build: (anchorId, otherId) => ({ type: 'partner', person_a_id: anchorId, person_b_id: otherId }),
  },
  {
    value: 'sibling',
    label: 'Add a sibling',
    hint: (name) => `${name} and the selected person are siblings.`,
    build: (anchorId, otherId) => ({ type: 'sibling', person_a_id: anchorId, person_b_id: otherId }),
  },
]

export default function AddRelationshipModal({ treeId, persons, onClose, onSaved, preselectedPersonId }) {
  const { addRelationship } = useTreeStore()

  // Guards the backdrop-to-close against touch scrolling: only close when the
  // pointer both went down AND came up on the backdrop itself. A scroll gesture
  // that starts inside the form and releases on the backdrop no longer dismisses
  // the modal mid-edit.
  const downOnBackdrop = useRef(false)

  const [anchorId, setAnchorId] = useState(preselectedPersonId || '')
  const [anchorSearch, setAnchorSearch] = useState('')
  const anchor = persons.find(p => p.id === anchorId)
  const anchorName = anchor
    ? (anchor.preferred_name || anchor.first_name || anchor.last_name || 'This person')
    : 'This person'

  const [role, setRole] = useState('child_of')
  // mode = 'existing' (pick from current tree) | 'new' (create + connect in one go)
  const [mode, setMode] = useState('existing')
  const [otherIds, setOtherIds] = useState([])
  const toggleOther = id => setOtherIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const [otherSearch, setOtherSearch] = useState('')
  // Second-parent picker — appears for parent_of (adding a child, who needs
  // both parents) and child_of (adding a parent, since the anchor child
  // typically has two). Always pulled from existing tree members; pick "—"
  // to skip and create a single-parent relationship.
  const [secondParentId, setSecondParentId] = useState('')
  const [secondParentSearch, setSecondParentSearch] = useState('')
  // Fields for the "create new person" form
  const [newFirst,  setNewFirst]  = useState('')
  const [newLast,   setNewLast]   = useState('')
  const [newBirth,  setNewBirth]  = useState('')
  const [newBirthPlace, setNewBirthPlace] = useState('')
  const [newDeath,  setNewDeath]  = useState('')
  const [newGender, setNewGender] = useState('')
  const [marriageDate, setMarriageDate] = useState('')
  const [marriagePlace, setMarriagePlace] = useState('')
  const [endDate, setEndDate] = useState('')
  const [endReason, setEndReason] = useState('') // '' | divorce | widowed | separated | annulled
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  // Dedup suggestions: populated by a debounced call as the user types
  // first/last name + birth year in 'new person' mode. `picked` is the
  // user-selected match that will short-circuit person creation on submit.
  const [suggestions, setSuggestions] = useState({ canonical: [], records: [], my_persons: [] })
  const [picked, setPicked] = useState(null) // { kind:'canonical'|'record'|'my_person', row }

  const selectedRole = ROLES.find(r => r.value === role)
  const others = persons.filter(p => p.id !== anchorId)

  // Dedup search — runs 300ms after the user stops typing in 'new' mode.
  // Only fires once last name is at least 2 chars to keep the endpoint
  // from getting hammered. Clears `picked` whenever the query changes.
  useEffect(() => {
    if (mode !== 'new') { setSuggestions({ canonical: [], records: [], my_persons: [] }); return }
    const ln = newLast.trim()
    if (ln.length < 2) { setSuggestions({ canonical: [], records: [], my_persons: [] }); return }
    const handle = setTimeout(async () => {
      try {
        const year = (newBirth.match(/\d{4}/) || [])[0]
        const params = new URLSearchParams({ last_name: ln })
        if (newFirst.trim()) params.set('first_name', newFirst.trim())
        if (year) params.set('birth_year', year)
        const data = await api.get(`/persons/dedup-search?${params.toString()}`)
        setSuggestions({
          canonical:  data?.canonical  || [],
          records:    data?.records    || [],
          my_persons: data?.my_persons || [],
        })
      } catch {
        setSuggestions({ canonical: [], records: [], my_persons: [] })
      }
    }, 300)
    return () => clearTimeout(handle)
  }, [mode, newFirst, newLast, newBirth])

  // If the user changes their form inputs, any previously-picked suggestion
  // becomes stale — clear it so they don't accidentally link to the wrong one.
  useEffect(() => { setPicked(null) }, [newFirst, newLast, newBirth, newBirthPlace, newDeath, newGender])

  function personLabel(p) {
    const name = [p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unknown'
    const year = p.birth_date?.match(/\d{4}/)?.[0]
    return year ? `${name} (${year})` : name
  }

  function personSearchKey(p) {
    return [p.first_name, p.preferred_name, p.last_name, p.birth_name, p.birth_place, p.birth_date]
      .filter(Boolean).join(' ').toLowerCase()
  }

  const q = otherSearch.trim().toLowerCase()
  const filteredOthers = q
    ? others.filter(p => personSearchKey(p).includes(q))
    : others
  const selectedOthers = others.filter(p => otherIds.includes(p.id))

  const aq = anchorSearch.trim().toLowerCase()
  const filteredAnchors = aq
    ? persons.filter(p => personSearchKey(p).includes(aq))
    : persons

  async function submit(e) {
    e.preventDefault()
    if (!anchorId) return setError('No anchor person set')
    setSaving(true)
    setError(null)
    try {
      // The set of "other" persons to connect. In 'existing' mode the user can
      // tick several at once (e.g. add three siblings in one pass); 'new' mode
      // resolves to exactly one created/claimed person.
      let targetIds = []

      if (mode === 'new') {
        let resolvedOtherId = ''
        // KG-first resolution:
        //   - picked.kind = 'canonical' → existing canonical person, just add to tree
        //   - picked.kind = 'record'    → KG record, claim (creates canonical if first time)
        //   - no pick                   → user confirmed this is truly new, create canonical
        if (picked?.kind === 'canonical' || picked?.kind === 'my_person') {
          const out = await api.post(`/persons/${picked.row.id}/add-to-tree`, { tree_id: treeId })
          resolvedOtherId = out.person.id
        } else if (picked?.kind === 'record') {
          const out = await api.post(`/records/${picked.row.id}/claim`, { tree_id: treeId })
          resolvedOtherId = out.person.id
        } else {
          if (!newFirst.trim() || !newLast.trim()) {
            setError('First and last name are required'); setSaving(false); return
          }
          const created = await api.post('/persons', {
            tree_id:      treeId,
            first_name:   newFirst.trim(),
            last_name:    newLast.trim(),
            birth_date:   newBirth.trim()   || null,
            birth_place:  newBirthPlace.trim() || null,
            death_date:   newDeath.trim()   || null,
            gender:       newGender || null,
          })
          resolvedOtherId = created.id
        }
        targetIds = [resolvedOtherId]
      } else {
        if (!otherIds.length) {
          setError('Select at least one person'); setSaving(false); return
        }
        targetIds = otherIds
      }

      const single = targetIds.length === 1
      for (const tid of targetIds) {
        const payload = selectedRole.build(anchorId, tid)
        // Marriage metadata only makes sense for a single partner — skip it
        // when several people were ticked at once.
        if (payload.type === 'partner' && single) {
          if (marriageDate.trim())  payload.marriage_date  = marriageDate.trim()
          if (marriagePlace.trim()) payload.marriage_place = marriagePlace.trim()
          if (endDate.trim())       payload.end_date       = endDate.trim()
          if (endReason)            payload.end_reason     = endReason
        }
        await addRelationship({ tree_id: treeId, ...payload })
      }

      // Second parent (only for parent_of / child_of, and only when a single
      // child/parent was added — ambiguous with a multi-select). The "child"
      // here is whichever side of the parent_child rel is the child:
      //   parent_of: anchor is parent, resolvedOther is child
      //   child_of:  resolvedOther is parent, anchor is child
      const resolvedOtherId = single ? targetIds[0] : null
      if (single && secondParentId && (role === 'parent_of' || role === 'child_of')) {
        const childId = role === 'parent_of' ? resolvedOtherId : anchorId
        if (secondParentId !== childId && secondParentId !== anchorId && secondParentId !== resolvedOtherId) {
          try {
            await addRelationship({
              tree_id: treeId,
              type: 'parent_child',
              person_a_id: secondParentId,
              person_b_id: childId,
            })
          } catch (err) {
            // The primary rel already saved — don't roll the user back to a
            // half-finished modal state. Surface a soft warning and close.
            console.warn('[addRel] second parent rel failed:', err.message)
          }
        }
      }

      onSaved?.()
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to add relationship')
      setSaving(false)
    }
  }

  return (
    <div
      className="modal-backdrop"
      onMouseDown={e => { downOnBackdrop.current = e.target === e.currentTarget }}
      onClick={e => {
        if (e.target === e.currentTarget && downOnBackdrop.current) onClose()
        downOnBackdrop.current = false
      }}
    >
      <div
        className="modal"
        style={{ maxHeight: '88vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label="Connect persons"
      >
        <div className="modal__header">
          <div className="modal__title">
            {anchor ? `Connect ${anchorName}` : 'Connect persons'}
          </div>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={submit} className="modal__body">

          {/* Anchor person picker — only shown when not preselected */}
          {!preselectedPersonId && (
            <div className="form-field" style={{ marginBottom: '1.25rem' }}>
              <label className="form-field__label" style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.78rem', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                First person
              </label>
              <input
                className="form-input"
                type="search"
                placeholder="Search by name, year, or place…"
                value={anchorSearch}
                onChange={e => setAnchorSearch(e.target.value)}
                style={{ marginBottom: '0.5rem' }}
              />
              <div style={{
                maxHeight: '180px', overflowY: 'auto',
                border: '1px solid var(--border)', borderRadius: '6px',
                background: 'white',
              }}>
                {filteredAnchors.length === 0 ? (
                  <div style={{ padding: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    No matches.
                  </div>
                ) : filteredAnchors.map(p => {
                  const active = p.id === anchorId
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { setAnchorId(p.id); setOtherIds([]) }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '0.5rem 0.75rem', fontSize: '0.85rem',
                        border: 'none', borderBottom: '1px solid var(--border)',
                        background: active ? 'color-mix(in srgb, var(--green) 10%, white)' : 'transparent',
                        color: 'var(--text)', cursor: 'pointer',
                      }}
                    >
                      {personLabel(p)}
                      {p.birth_place && (
                        <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem', fontSize: '0.78rem' }}>
                          · {p.birth_place}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
              {anchor && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                  Selected: <strong>{personLabel(anchor)}</strong>
                </div>
              )}
            </div>
          )}

          {/* Role selector */}
          <div className="form-field" style={{ marginBottom: '1.25rem' }}>
            <label className="form-field__label" style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.78rem', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              Relationship type
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {ROLES.map(r => {
                const active = role === r.value
                return (
                  <label key={r.value} style={{
                    display: 'flex',
                    // Explicit row — `.modal label` global rule otherwise
                    // forces flex-direction: column and stacks the radio
                    // above the text.
                    flexDirection: 'row',
                    alignItems: 'center',
                    // Plus text-transform/letter-spacing — `.modal label`
                    // is also uppercase-muted by default; we need the
                    // role cards to read as normal prose.
                    textTransform: 'none',
                    letterSpacing: 'normal',
                    color: 'inherit',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem', borderRadius: '6px', cursor: 'pointer',
                    border: `1.5px solid ${active ? 'var(--green)' : 'var(--border)'}`,
                    background: active ? 'color-mix(in srgb, var(--green) 5%, white)' : 'white',
                    transition: 'all 0.15s'
                  }}>
                    <input type="radio" name="role" value={r.value}
                      checked={active} onChange={() => { setRole(r.value); setOtherIds([]); setSecondParentId('') }}
                      style={{ accentColor: 'var(--green)', width: '16px', height: '16px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text)' }}>
                        {r.label}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {r.hint(anchorName)}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Mode toggle: pick existing person vs create new + connect */}
          <div className="form-field" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.35rem', background: 'var(--paper)', padding: '0.25rem', borderRadius: 8, border: '1px solid var(--border)' }}>
              {[
                { id: 'existing', label: 'Pick from tree' },
                { id: 'new',      label: 'Add new person' },
              ].map(opt => {
                const active = mode === opt.id
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => { setMode(opt.id); setError(null) }}
                    style={{
                      flex: 1, padding: '0.55rem 0.8rem', border: 'none',
                      borderRadius: 6, cursor: 'pointer',
                      background: active ? '#fff' : 'transparent',
                      color: active ? 'var(--text)' : 'var(--text-muted)',
                      fontWeight: active ? 600 : 500,
                      fontSize: '0.85rem',
                      boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                    }}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          {mode === 'new' && (
            <div className="form-field" style={{ marginBottom: '1.25rem' }}>
              <label className="form-field__label" style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.78rem', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                New person
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <input className="form-input" type="text" placeholder="First name *" required
                  value={newFirst} onChange={e => setNewFirst(e.target.value)} autoFocus />
                <input className="form-input" type="text" placeholder="Last name *" required
                  value={newLast} onChange={e => setNewLast(e.target.value)} />
                <PlaceInput placeholder="Birth place"
                  value={newBirthPlace} onChange={setNewBirthPlace} />
                <select className="form-input" value={newGender}
                  onChange={e => setNewGender(e.target.value)}>
                  <option value="">Gender — optional</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{ marginTop: '0.6rem' }}>
                <label style={{ display: 'block', fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Birth date</label>
                <PartialDateInput value={newBirth} onChange={setNewBirth} />
              </div>
              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                  Death date <em style={{ fontStyle: 'normal', opacity: 0.7 }}>(optional)</em>
                </label>
                <PartialDateInput value={newDeath} onChange={setNewDeath} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                The new person is added to this tree and connected in one step. You can fill in more detail from their profile afterwards.
              </div>

              {/* Dedup suggestions — your own trees first, then KG + canonical persons elsewhere */}
              {(suggestions.canonical.length + suggestions.records.length + suggestions.my_persons.length) > 0 && (
                <div style={{
                  marginTop: '0.75rem', padding: '0.75rem 0.85rem',
                  border: '1px solid var(--gold)', borderRadius: 8,
                  background: 'color-mix(in srgb, var(--gold) 7%, white)',
                }}>
                  <div style={{
                    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '0.5rem',
                  }}>
                    Already in your data
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem', lineHeight: 1.4 }}>
                    {picked
                      ? `You'll link to this existing person. Change any field to disable.`
                      : 'This person may already exist. Pick one below to add them to this tree without duplicating — or ignore and create new.'}
                  </div>
                  {[
                    // Your own trees first — most actionable, includes living relatives
                    ...suggestions.my_persons.map(p => ({ kind: 'my_person', row: p })),
                    ...suggestions.canonical.map(c => ({ kind: 'canonical', row: c })),
                    ...suggestions.records.map(r => ({ kind: 'record', row: r })),
                  ].slice(0, 8).map(item => {
                    const r = item.row
                    const name = [r.preferred_name || r.first_name, r.last_name].filter(Boolean).join(' ') || 'Unknown'
                    const year = item.kind === 'record'
                      ? (r.birth_year || (r.birth_date?.match(/\d{4}/) || [])[0])
                      : (r.birth_date?.match(/\d{4}/) || [])[0]
                    const place = r.birth_place || ''
                    const origin = item.kind === 'my_person'
                      ? `In your tree${(r.in_trees?.length || 0) === 1 ? '' : 's'}: ${(r.in_trees || []).join(', ')}`
                      : item.kind === 'canonical'
                        ? `Public · in ${r.tree_count || 1} tree${r.tree_count === 1 ? '' : 's'}`
                        : `Knowledge Graph · ${r.source_archive || 'record'}`
                    const isPicked = picked?.kind === item.kind && picked?.row?.id === r.id
                    return (
                      <button
                        key={`${item.kind}-${r.id}`}
                        type="button"
                        onClick={() => setPicked(isPicked ? null : item)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '0.5rem 0.7rem', marginBottom: '0.3rem',
                          borderRadius: 6, cursor: 'pointer',
                          border: `1.5px solid ${isPicked ? 'var(--green)' : 'var(--border)'}`,
                          background: isPicked ? 'color-mix(in srgb, var(--green) 10%, white)' : 'white',
                        }}
                      >
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>
                          {name}{year ? ` (${year})` : ''}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                          {[place, origin].filter(Boolean).join(' · ')}
                          {isPicked && ' · ✓ selected'}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Person picker with search */}
          {mode === 'existing' && (
          <div className="form-field" style={{ marginBottom: '1.25rem' }}>
            <label className="form-field__label" style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.78rem', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              {role === 'child_of' ? 'Select parent(s)' :
               role === 'parent_of' ? 'Select child(ren)' :
               role === 'partner' ? 'Select partner' : 'Select sibling(s)'}
              <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.4rem' }}>
                — tick several to add them all
              </span>
            </label>
            {others.length === 0 ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '0.75rem', background: 'var(--surface)', borderRadius: '6px' }}>
                No other persons in this tree yet. Add more people first.
              </div>
            ) : (
              <>
                <input
                  className="form-input"
                  type="search"
                  placeholder="Search by name, year, or place…"
                  value={otherSearch}
                  onChange={e => setOtherSearch(e.target.value)}
                  style={{ marginBottom: '0.5rem' }}
                />
                <div style={{
                  maxHeight: '220px', overflowY: 'auto',
                  border: '1px solid var(--border)', borderRadius: '6px',
                  background: 'white',
                }}>
                  {filteredOthers.length === 0 ? (
                    <div style={{ padding: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      No matches.
                    </div>
                  ) : filteredOthers.map(p => {
                    const active = otherIds.includes(p.id)
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleOther(p.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          width: '100%', textAlign: 'left',
                          padding: '0.5rem 0.75rem', fontSize: '0.85rem',
                          border: 'none', borderBottom: '1px solid var(--border)',
                          background: active ? 'color-mix(in srgb, var(--green) 12%, white)' : 'transparent',
                          color: 'var(--text)', cursor: 'pointer',
                        }}
                      >
                        <span aria-hidden style={{ width: '1.1em', flex: 'none', color: 'var(--green)' }}>{active ? '✓' : ''}</span>
                        {personLabel(p)}
                        {p.birth_place && (
                          <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem', fontSize: '0.78rem' }}>
                            · {p.birth_place}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
                {selectedOthers.length > 0 && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                    Selected ({selectedOthers.length}): <strong>{selectedOthers.map(personLabel).join(', ')}</strong>
                  </div>
                )}
              </>
            )}
          </div>
          )}

          {(role === 'child_of' || role === 'parent_of') && (() => {
            // Second parent picker. Always picked from existing tree members
            // — a brand-new second parent should be added in their own pass.
            // Excluded from the candidate list: the anchor and the selected
            // "other" person (they're the first parent or the child here).
            const candidates = persons.filter(p =>
              p.id !== anchorId && !otherIds.includes(p.id)
            )
            const sq = secondParentSearch.trim().toLowerCase()
            const filteredSecond = sq
              ? candidates.filter(p => personSearchKey(p).includes(sq))
              : candidates
            const selectedSecond = candidates.find(p => p.id === secondParentId)
            const fieldLabel = role === 'parent_of'
              ? 'Other parent (optional)'
              : 'Other parent of this child (optional)'
            const fieldHint = role === 'parent_of'
              ? `Save once and ${anchorName} + the selected partner are both linked as parents to the new child.`
              : `Adds the existing person as a second parent to ${anchorName} in the same step.`

            return (
              <div className="form-field" style={{ marginBottom: '1.25rem' }}>
                <label className="form-field__label" style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.78rem', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                  {fieldLabel}
                </label>
                {candidates.length === 0 ? (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '0.75rem', background: 'var(--surface)', borderRadius: '6px' }}>
                    No other tree members yet — you can connect the second parent later.
                  </div>
                ) : (
                  <>
                    <input
                      className="form-input"
                      type="search"
                      placeholder="Search by name, year, or place…"
                      value={secondParentSearch}
                      onChange={e => setSecondParentSearch(e.target.value)}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    <div style={{
                      maxHeight: '160px', overflowY: 'auto',
                      border: '1px solid var(--border)', borderRadius: '6px',
                      background: 'white',
                    }}>
                      <button
                        type="button"
                        onClick={() => setSecondParentId('')}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '0.5rem 0.75rem', fontSize: '0.85rem',
                          border: 'none', borderBottom: '1px solid var(--border)',
                          background: !secondParentId ? 'color-mix(in srgb, var(--green) 10%, white)' : 'transparent',
                          color: 'var(--text-muted)', cursor: 'pointer', fontStyle: 'italic',
                        }}
                      >
                        — None / skip
                      </button>
                      {filteredSecond.length === 0 ? (
                        <div style={{ padding: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                          No matches.
                        </div>
                      ) : filteredSecond.map(p => {
                        const active = p.id === secondParentId
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setSecondParentId(p.id)}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left',
                              padding: '0.5rem 0.75rem', fontSize: '0.85rem',
                              border: 'none', borderBottom: '1px solid var(--border)',
                              background: active ? 'color-mix(in srgb, var(--green) 10%, white)' : 'transparent',
                              color: 'var(--text)', cursor: 'pointer',
                            }}
                          >
                            {personLabel(p)}
                            {p.birth_place && (
                              <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem', fontSize: '0.78rem' }}>
                                · {p.birth_place}
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                    {selectedSecond && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                        Will link as second parent: <strong>{personLabel(selectedSecond)}</strong>
                      </div>
                    )}
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                      {fieldHint}
                    </div>
                  </>
                )}
              </div>
            )
          })()}

          {role === 'partner' && (
            <div className="form-field" style={{ marginBottom: '1.25rem' }}>
              <label className="form-field__label" style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.78rem', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                Marriage (optional)
              </label>
              <label style={{ display: 'block', fontSize: '0.74rem', color: 'var(--text-muted)', margin: '0 0 0.3rem' }}>Marriage date</label>
              <PartialDateInput value={marriageDate} onChange={setMarriageDate} />
              <div style={{ marginTop: '0.5rem' }}>
                <PlaceInput
                  placeholder="Marriage place — e.g. Amsterdam"
                  value={marriagePlace}
                  onChange={setMarriagePlace}
                />
              </div>
              <div style={{ marginTop: '0.5rem' }}>
                <select
                  className="form-input"
                  value={endReason}
                  onChange={e => setEndReason(e.target.value)}
                >
                  <option value="">Still together</option>
                  <option value="divorce">Divorced</option>
                  <option value="widowed">Widowed</option>
                  <option value="separated">Separated</option>
                  <option value="annulled">Annulled</option>
                </select>
              </div>
              {endReason && (
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.74rem', color: 'var(--text-muted)', margin: '0 0 0.3rem' }}>End date</label>
                  <PartialDateInput value={endDate} onChange={setEndDate} />
                </div>
              )}
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                Leave blank if unknown — you can always fill this in later. For remarriages, add the new partner as a separate partner relationship.
              </div>
            </div>
          )}

          {error && <div className="form-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                saving ||
                (mode === 'existing' && otherIds.length === 0) ||
                (mode === 'new' && (!newFirst.trim() || !newLast.trim()))
              }
            >
              {saving
                ? 'Saving…'
                : mode === 'new'
                  ? (picked?.kind === 'canonical' ? 'Link existing person & connect'
                     : picked?.kind === 'record'  ? 'Claim from KG & connect'
                     : 'Add new person & connect')
                  : 'Add connection'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  )
}
